import { listUserAnimations, setAnimationPublicById, deleteAnimationById } from '../lib/storage.js';
import { isAuthenticated, onAuthChange } from '../lib/auth.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import { showToast, showConfirmModal } from '../editor/ui.js';

let authUnsub = null;

export function initMyAnimations() {
  const container = document.getElementById('my-anim-container');
  if (!container) return;

  render(container);

  // Refresh on auth changes so sign-in/out swaps the view
  if (!authUnsub) {
    authUnsub = onAuthChange(() => {
      if (document.getElementById('page-my-animations')?.classList.contains('active')) {
        render(container);
      }
    });
  }
}

export function cleanupMyAnimations() {
  if (authUnsub) { authUnsub(); authUnsub = null; }
}

function render(container) {
  container.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'my-anim-header';
  header.innerHTML = `
    <h1>My Animations</h1>
    <p>All the timelines you've saved to the cloud.</p>
  `;
  container.appendChild(header);

  if (!isSupabaseConfigured()) {
    const msg = document.createElement('div');
    msg.className = 'my-anim-empty';
    msg.textContent = 'Cloud storage is not configured in this build.';
    container.appendChild(msg);
    return;
  }

  if (!isAuthenticated()) {
    const msg = document.createElement('div');
    msg.className = 'my-anim-empty';
    msg.innerHTML = `
      <p>Sign in to see your cloud animations.</p>
      <button class="my-anim-signin">Sign in</button>
    `;
    msg.querySelector('.my-anim-signin').addEventListener('click', () => {
      document.querySelector('.auth-signin-btn')?.click();
    });
    container.appendChild(msg);
    return;
  }

  const list = document.createElement('div');
  list.className = 'my-anim-list';
  container.appendChild(list);

  const loading = document.createElement('div');
  loading.className = 'my-anim-loading';
  loading.textContent = 'Loading...';
  list.appendChild(loading);

  listUserAnimations()
    .then(animations => {
      list.innerHTML = '';
      if (!animations.length) {
        const empty = document.createElement('div');
        empty.className = 'my-anim-empty';
        empty.innerHTML = `
          <p>No saved animations yet.</p>
          <a href="#/editor" class="explore-cta">Open Editor</a>
        `;
        list.appendChild(empty);
        return;
      }
      for (const a of animations) {
        list.appendChild(renderCard(a, container));
      }
    })
    .catch(e => {
      list.innerHTML = '';
      const err = document.createElement('div');
      err.className = 'my-anim-empty';
      err.textContent = 'Failed to load: ' + e.message;
      list.appendChild(err);
    });
}

function renderCard(a, container) {
  const card = document.createElement('div');
  card.className = 'my-anim-card';

  const preview = document.createElement('div');
  preview.className = 'my-anim-preview';
  preview.style.color = a.thumbnail_color || '#ca4b15';
  preview.textContent = a.thumbnail_text || '';
  card.appendChild(preview);

  const body = document.createElement('div');
  body.className = 'my-anim-body';

  const title = document.createElement('div');
  title.className = 'my-anim-title';
  title.textContent = a.title || 'Untitled';
  body.appendChild(title);

  const meta = document.createElement('div');
  meta.className = 'my-anim-meta';
  const badge = document.createElement('span');
  badge.className = 'seq-badge ' + (a.is_public ? 'seq-badge-public' : 'seq-badge-saved');
  badge.textContent = a.is_public ? '\u{1F310} Public' : '\u25CF Private';
  meta.appendChild(badge);
  const updated = document.createElement('span');
  updated.className = 'my-anim-updated';
  updated.textContent = 'Updated ' + new Date(a.updated_at).toLocaleString();
  meta.appendChild(updated);
  body.appendChild(meta);

  card.appendChild(body);

  const actions = document.createElement('div');
  actions.className = 'my-anim-actions';

  const viewBtn = document.createElement('a');
  viewBtn.className = 'btn-sm';
  viewBtn.textContent = 'View';
  viewBtn.href = '#/view/' + a.id;
  actions.appendChild(viewBtn);

  const pubBtn = document.createElement('button');
  pubBtn.className = 'btn-sm';
  pubBtn.textContent = a.is_public ? 'Unpublish' : 'Publish';
  pubBtn.addEventListener('click', async () => {
    try {
      await setAnimationPublicById(a.id, !a.is_public);
      showToast(a.is_public ? 'Unpublished' : 'Published!');
      render(container);
    } catch (e) {
      showToast('Failed: ' + e.message);
    }
  });
  actions.appendChild(pubBtn);

  const delBtn = document.createElement('button');
  delBtn.className = 'btn-sm btn-danger';
  delBtn.textContent = 'Delete';
  delBtn.addEventListener('click', () => {
    showConfirmModal('Delete "' + (a.title || 'Untitled') + '" from the cloud?', async () => {
      try {
        await deleteAnimationById(a.id);
        showToast('Deleted');
        render(container);
      } catch (e) {
        showToast('Delete failed: ' + e.message);
      }
    });
  });
  actions.appendChild(delBtn);

  card.appendChild(actions);
  return card;
}
